import { motion } from 'framer-motion';
import React from 'react';
import styled from 'styled-components';

export interface MotionItemProps {
    id: string;
    top: string;
    left: string;
    width: string;
    rotate: string;
    z: number;
    src?: string;
    alt?: string;
    children?: React.ReactNode;
    onPress?: () => void;
    className?: string;
    transitionDurationMs?: number;
    hoverEnabled?: boolean;
    clickable?: boolean;
    onPointerDown?: (e: React.PointerEvent) => void;
    onPointerUp?: (e: React.PointerEvent) => void;
    onPointerCancel?: (e: React.PointerEvent) => void;
    onPointerLeave?: (e: React.PointerEvent) => void;
}

/**
 * Static motion-driven item (no drag). Animates between poses using Framer Motion.
 * Keeps simple hover effects via CSS (consumer styles).
 */
export function MotionItem({
    id,
    top,
    left,
    width,
    rotate,
    z,
    src,
    alt,
    children,
    onPress,
    className,
    transitionDurationMs = 220,
    hoverEnabled = true,
    clickable,
    onPointerDown,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
}: MotionItemProps) {
    const baseRotateDeg = parseFloat(rotate || '0');
    // Default to true if onPress exists, but can be overridden
    const hasClickAction = clickable !== undefined ? clickable : !!onPress;
    return (
        <StyledMotionDiv
            $z={z}
            $hoverEnabled={hoverEnabled}
            $hasClickAction={hasClickAction}
            data-item-id={id}
            data-hover-enabled={hoverEnabled ? 'true' : 'false'}
            className={className}
            initial={false}
            animate={{
                top,
                left,
                width,
                rotateZ: baseRotateDeg,
            }}
            transition={{ duration: transitionDurationMs / 1000, ease: [0.4, 0, 0.2, 1] }}
            whileHover={
                hoverEnabled
                    ? {
                        y: -6,
                        rotateZ: baseRotateDeg + 2,
                        transition: {
                            // Make hover snappy and independent from scatter duration
                            duration: 0.32,
                            ease: [0.22, 1, 0.36, 1],
                        },
                    }
                    : undefined
            }
            whileTap={
                hasClickAction && !hoverEnabled
                    ? {
                        y: -6,
                        rotateZ: baseRotateDeg + 2,
                        transition: {
                            duration: 0.32,
                            ease: [0.22, 1, 0.36, 1],
                        },
                    }
                    : undefined
            }
            onClick={onPress}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onPointerLeave={onPointerLeave}
        >
            {src ? (
                <StyledImage
                    src={src}
                    alt={alt}
                    draggable={false}
                />
            ) : (
                children
            )}
        </StyledMotionDiv>
    );
}

const StyledImage = styled.img.attrs({
    // Use async decoding to avoid blocking main thread
    // Note: Don't use loading="lazy" here - these items are often above-the-fold
    // and the useImagePreloader hook handles critical image preloading
    decoding: 'async',
})`
    width: 100%;
    height: auto;
    display: block;
    
    pointer-events: none;         /* prevents image preview */
    -webkit-user-drag: none;
    user-drag: none;
`;

const StyledMotionDiv = styled(motion.div)<{ $z: number; $hoverEnabled: boolean; $hasClickAction: boolean }>`
    position: absolute;
    z-index: ${({ $z }) => $z};
    transform-origin: center bottom;
    will-change: transform;
    
    filter: drop-shadow(0 8px 12px rgba(0, 0, 0, 0.35));
    
    /* Add pointer cursor when item has a click action */
    cursor: ${({ $hasClickAction }) => ($hasClickAction ? 'pointer' : 'default')};
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
    touch-action: manipulation;   /* disables default long-press behavior */
    
    /* Expose hover state to descendants (e.g., Matchbox CSS) via pure CSS */
    --is-hovered: 0;
    
    /* Set hover state via CSS when hover is enabled */
    &[data-hover-enabled='true']:hover {
        --is-hovered: 1;
    }
`;

